import React, { useState, useMemo } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import RecentSoldPropertySlider from "../common/RecentSoldProperties/RecentSoldPropertySlider";
import Googlemap2 from "../common/GoogleMap/Googlemap2";
import { AuthModal } from "../../models/AuthModal";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";
import { toast } from "react-toastify";
import CollectionPropertyCard from "./CollectionPropertyCard";

const FILTER_TABS = ["All", "Buy", "Rent", "Sold", "Inspected"];

function getPropertyStatus(p) {
  const s = (p?.propertyId?.status || "").toString().toLowerCase();
  if (s.includes("sold")) return "Sold";
  if (s.includes("active")) return "Buy";
  if (s.includes("rent") || s.includes("lease")) return "Rent";
  if (s.includes("inspect")) return "Inspected";
  return "Buy";
}

export default function CollectionDetailView({
  collectionName,
  description,
  properties = [],
  onBack,
  isLoading,
}) {
  const [filter, setFilter] = useState("All");
  const [mapVisible, setMapVisible] = useState(true);
  const [savedIds, setSavedIds] = useState(
    () => localStorage.getItem("Saved")?.split(",").filter(Boolean) || [],
  );
  const [authPopUp, setAuthPopUp] = useState(false);
  const isAuthenticate = !!localStorage.getItem("Token");

  const filteredProperties = useMemo(() => {
    if (!Array.isArray(properties)) return [];
    if (filter === "All") return properties;
    return properties.filter((p) => getPropertyStatus(p) === filter);
  }, [properties, filter]);

  const mapLocations = useMemo(() => {
    if (!Array.isArray(properties)) return [];
    return properties.map((p) => p?.propertyId);
  }, [properties]);

  const handleFavorite = async (item) => {
    const id = item?.id ?? item?._id;
    if (!id) return;
    try {
      const res = await axiosInstanceAuth.post("addToFavorites", { id });
      if (res?.data?.status) {
        setSavedIds((prev) =>
          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
        toast.success(res?.data?.message ?? "Saved");
      } else {
        toast.error(res?.data?.message ?? "Failed");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-[#097FE6] font-medium hover:underline"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to collection
        </button>
        <div className="min-h-[200px] flex items-center justify-center rounded-2xl bg-white border border-gray-100 text-gray-500">
          Loading properties...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4 py-16 overflow-scroll">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-[#097FE6] font-medium hover:underline"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
            Back to collection
          </button>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#000929]">
                {collectionName}
              </h2>
              {/* {description ? (
              <p className="text-gray-600 mt-1 max-w-2xl">{description}</p>
            ) : null} */}
              <p className="text-gray-500 text-sm mt-2">
                Showing {filteredProperties.length} propert
                {filteredProperties.length === 1 ? "y" : "ies"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMapVisible((v) => !v)}
              className="inline-flex items-center gap-1 text-[#097FE6] font-medium hover:underline shrink-0"
            >
              {mapVisible ? "Hide map" : "Show map"}
              <ChevronRight
                className={`w-4 h-4 transition-transform ${mapVisible ? "rotate-90" : ""}`}
              />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setFilter(tab)}
                className={`shrink-0 rounded-full px-5 py-2 text-sm font-medium transition text-[#000929] ${filter === tab
                  ? "bg-white border-2 border-[#000929]"
                  : "bg-white border border-gray-200 hover:border-gray-300"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="flex flex-col lg:flex-row gap-6">
            <div className={`flex-1 min-w-0 `}>
              {!filteredProperties.length ? (
                <div className="min-h-[200px] flex flex-col items-center justify-center rounded-2xl bg-[#E9EDF2] border border-gray-200 text-center px-6 py-12">
                  <p className="text-[#000929] font-medium">
                    No properties in this collection yet.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    Tap the star on any property to add it here.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto">
                  {filteredProperties.map((item, index) => (
                    <CollectionPropertyCard
                      key={
                        item?.id ?? item?._id ?? index ?? item?.propertyId?.id
                      }
                      property={item.propertyId}
                      isFavorite={item?.propertyId?.collections?.length > 0}
                      isAuthenticate={isAuthenticate}
                      setAuthPopUp={setAuthPopUp}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {mapVisible && (
          <div className="w-full h-full ">
            <Googlemap2 location={mapLocations} style={{ height: "100%  " }} />
          </div>
        )}
      </div>

      {authPopUp && (
        <AuthModal open={authPopUp} onClose={() => setAuthPopUp(false)} />
      )}
    </>
  );
}
